export type Owner = "kolya" | "vlad";
export type Currency = "RUB" | "USDT" | "USD" | "CNY";
export type Shares = { kolya: number; vlad: number };
export interface Account {
  id: string;
  name: string;
  kind: string;
  currency: Currency;
  revision: number;
  balance_minor: number;
  owner_balances: Shares;
}
export interface Lot {
  id: string;
  account_id: string;
  owner_party: Owner;
  currency: Currency;
  status: string;
  acquired_at: string;
  cycle_id?: string;
  source_document_id: string;
  remaining_amount_minor: number;
  initial_amount_minor: number;
  rub_cost_minor: number | null;
  remaining_rub_cost_minor: number | null;
}
export interface Funding {
  owner_party: Owner;
  amount_minor: number;
  currency?: Currency;
  rub_cost_minor: number | null;
  lot_id?: string;
}
export interface CycleCancellation {
  status?: string;
  request_id?: string;
  revision?: number;
  requester_party?: Owner;
  reason?: string;
  participants?: Owner[];
  approved_owners?: Owner[];
  waiting_for?: Owner[];
  frozen?: boolean;
  allowed_actions?: string[];
  blocked_reason?: string;
  blocking_document_id?: string;
  return_amounts_rub_minor?: Partial<Record<Owner, number>>;
}
export interface OperationInput {
  kind?: string;
  owner_party?: Owner;
  amount?: string;
  actual_amount?: string;
  source_amount?: string;
  target_amount?: string;
  owner_amounts?: Partial<Record<Owner, string>>;
  source_account_id?: string;
  target_account_id?: string;
  cash_out?: boolean;
  description?: string;
  rub_cost?: string;
  reason?: string;
  expense_split?: Partial<Record<Owner, string>>;
  allocations?: Allocation[];
}
export interface MoneyDocument {
  id: string;
  revision: number;
  kind: string;
  status: string;
  rub_movements?: { account_id: string; amount_minor: number }[];
  is_reversed?: boolean;
  reversed_at?: string;
  reversed_document_id?: string;
  description?: string;
  occurred_at?: string;
  created_at?: string;
  posted_at?: string;
  source_amount_minor?: number;
  source_currency?: Currency;
  target_amount_minor?: number;
  target_currency?: Currency;
  source_cost_rub_minor?: number;
  retained_inspection_valuation?: {
    amount_cny_minor: number;
    rub_cost_minor: number | null;
    cost_pending: boolean;
  };
  cancellation?: CycleCancellation;
  metadata: {
    exchange_rate?: { pair: string; hundredths: number };
    provisional_valuation?: { rate_rounding_rub_minor?: number | null; valued_rub_minor?: number | null };
    rounding_remainders?: { currency: Currency; amount_minor: number; source_amount_minor: number; allocated_amount_minor: number; reason: string }[];
    input?: OperationInput;
    owner_party?: Owner;
    sku?: string;
    products?: { product_id?: string; sku: string }[];
    avg_rate?: {
      avg_rate_hundredths?: number | null;
      rate_rounding_rub_minor?: number | null;
      lot_rounding_rub_minor?: number | null;
      fee_rub_minor: number;
      total_cny_minor: number;
      net_rub_minor: number;
      avg_rub_per_cny: number | null;
      products_finalized: number;
    };
    owner_amounts_rub_minor?: Shares;
    lots?: Funding[];
    funding?: Funding[];
    source_account_id?: string;
    target_account_id?: string;
    expense_rub_minor?: number;
    expense_split_rub?: Shares;
    void_reason?: string;
    shipment_code?: string;
    cash_out?: boolean;
    mixed_wallet_topup?: boolean;
    difference_minor?: number;
    expected_minor?: number;
    actual_minor?: number;
    reason?: string;
  };
}
export interface CycleEvent {
  document_id: string;
  kind: string;
  label: string;
  status: string;
  occurred_at: string;
  description?: string;
  source_account_id?: string;
  target_account_id?: string;
  source_amount_minor?: number;
  source_currency?: Currency;
  target_amount_minor?: number;
  target_currency?: Currency;
  product_skus?: string[];
}
export interface Cycle {
  is_paused?: boolean;
  start_rate_hundredths?: number;
  avg_rate_hundredths?: number | null;
  close_conditions?: { id: string; label: string; fulfilled: boolean; amount_minor: number; currency: Currency; limit_minor?: number }[];
  close_conditions_completed?: number;
  id: string;
  revision: number;
  owner_party: Owner;
  status: string;
  display_status: string;
  created_at: string;
  closed_at?: string;
  opening_document_id: string;
  source_rub_minor: number;
  target_usdt_minor: number;
  wallet_usdt_minor: number;
  htx_usdt_minor: number;
  usdt_remaining_minor: number;
  alipay_cny_minor: number;
  prior_closed_tail_cny_minor: number;
  fee_rub_minor?: number | null;
  total_cny_minor?: number | null;
  net_rub_minor?: number | null;
  avg_rub_per_cny?: number | null;
  close_preview?: {
    avg_rate_hundredths?: number | null;
    fee_rub_minor: number;
    total_cny_minor: number;
    net_rub_minor: number;
    avg_rub_per_cny: number | null;
  };
  is_joint?: boolean;
  joint_owners?: Owner[];
  cancellation?: CycleCancellation;
  events?: CycleEvent[];
}
export interface FeeReserve {
  id: string;
  revision: number;
  status: string;
  currency: Currency;
  total_amount_minor: number;
}
export interface AccountingSnapshot {
  accounts: Account[];
  documents: MoneyDocument[];
  lots: Lot[];
  cycles: Cycle[];
  fee_reserves: FeeReserve[];
  currency_digits: Partial<Record<Currency, number>>;
  money_schema_version?: string | number;
}
export interface CommerceProduct {
  has_purchase_comment?: boolean;
  id: string;
  revision: number;
  sku: string;
  description?: string;
  title?: string;
  status: string;
  logistics_status: string;
  shipment_code?: string;
  funding: Funding[];
  purchase_cost?: number;
  purchased_at?: string;
  arrival_date?: string;
  fulfillment_mode?: string;
  settlement_status?: string;
  preorder_customer?: string | null;
}
export interface PurchaseItem {
  product_id?: string;
  description?: string;
  currency?: Currency;
  total_amount_minor?: number;
  funding?: Funding[];
}
export interface Purchase {
  id: string;
  revision: number;
  status: string;
  product_id?: string;
  description?: string;
  currency: Currency;
  total_amount_minor?: number;
  payment_document_id?: string;
  occurred_at?: string;
  bought_at?: string;
  created_at?: string;
  items: PurchaseItem[];
}
export type PurchaseStage =
  | "active"
  | "inspection"
  | "china_transit"
  | "actions"
  | "purchased"
  | "china"
  | "transit"
  | "moscow"
  | "ready"
  | "history";
export type PurchaseNextAction =
  | "receive_china"
  | "start_inspection"
  | "finish_inspection"
  | "add_to_shipment"
  | "open_shipment"
  | "add_photos"
  | "set_telegram_price"
  | "prepare_for_content"
  | "start_processing"
  | "mark_ready"
  | "reconcile";
export interface PurchaseListItem {
  has_purchase_comment?: boolean;
  id: string;
  product_id?: string | null;
  product_revision?: number | null;
  sku?: string | null;
  title?: string | null;
  amount_minor: number;
  purchase_amount_minor?: number;
  inspection_required?: boolean;
  inspection_amount_minor?: number;
  currency: Currency;
  stage: string;
  logistics_status: string;
  status: string;
  shipment_code?: string | null;
  has_photos?: boolean;
  has_seller_photo?: boolean;
  thumbnail_source?: "own" | "seller" | null;
  next_action?: PurchaseNextAction | null;
  blocker?: "photos_required" | "telegram_price_required" | "cost_pending" | "reconciliation_required" | null;
  cost_pending?: boolean;
  rub_cost_minor?: number | null;
}
export interface PurchaseListDocument {
  id: string;
  purchase_id: string;
  revision: number;
  status: string;
  bought_at?: string | null;
  occurred_at?: string | null;
  owner_party?: Owner | null;
  total_amount_minor: number;
  currency: Currency;
  cost_pending?: boolean;
  total_rub_cost_minor?: number | null;
  can_cancel?: boolean;
  has_money_movement?: boolean;
  payment_document_id?: string | null;
  cancel_refund_amount_minor?: number;
  retained_inspection_amount_minor?: number;
  items: PurchaseListItem[];
}
export interface PurchaseListResponse {
  items: PurchaseListDocument[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  counts_by_stage: Record<PurchaseStage, number>;
}
export interface ShipmentItem {
  has_purchase_comment?: boolean;
  product_id: string;
  sku: string;
  system_number?: string;
  description?: string;
  title?: string;
  fulfillment_mode?: string;
  product_status?: string;
  logistics_status?: string;
  settlement_status?: string | null;
  preorder_customer?: string | null;
  preorder_id?: string | null;
  funding_owner?: Owner | null;
  delivery_amount_minor?: number | null;
  delivery_status?: "pending" | "paid" | "not_required";
}
export type ShipmentDeliveryStatus =
  | "not_set"
  | "pending"
  | "partially_paid"
  | "paid"
  | "paid_legacy";
export interface ShipmentDeliveryObligation {
  id: string;
  source_type: "owner" | "preorder";
  owner_party?: Owner | null;
  preorder_id?: string | null;
  label: string;
  amount_minor: number;
  paid_minor: number;
  available_minor: number;
  deficit_minor: number;
  status: "pending" | "paid";
}
export interface ShipmentDeliveryPlan {
  mode: "total" | "per_item" | "legacy";
  total_minor: number;
  unit_minor?: number | null;
  created_at?: string;
  corrected_at?: string | null;
}
export interface Shipment {
  code: string;
  tracking_number?: string;
  short_name?: string | null;
  history?: { id?: number; at: string; action: string; actor?: number | string | null; actor_role?: string; before?: Record<string, unknown>; after?: Record<string, unknown> }[];
  revision: number;
  status: string;
  composition_closed_at?: string;
  created_at?: string;
  shipped_at?: string;
  received_at?: string;
  delivery_total_minor: number | null;
  delivery_paid_minor?: number;
  delivery_status?: ShipmentDeliveryStatus;
  delivery_plan?: ShipmentDeliveryPlan | null;
  delivery_obligations?: ShipmentDeliveryObligation[];
  items: ShipmentItem[];
  costs: { status: string }[];
}
export interface ShipmentDeliveryPreview {
  preview_token: string;
  mode: "total" | "per_item";
  total_minor: number;
  unit_minor?: number | null;
  items: {
    product_id: string;
    sku: string;
    amount_minor: number;
    source_type: "owner" | "preorder";
    owner_party?: Owner | null;
    preorder_id?: string | null;
    label: string;
  }[];
  obligations: ShipmentDeliveryObligation[];
}
export interface CommerceSnapshot {
  products: CommerceProduct[];
  purchases: Purchase[];
  shipments: Shipment[];
}
export interface ShipmentListResponse {
  items: Shipment[];
  total: number;
  page: number;
  page_size: number;
  counts_by_stage: Record<string, number>;
}
export interface HistoricExpense {
  id: string;
  revision: number;
  status: string;
  day: string;
  category: string;
  description: string;
  amount: number;
  payer: Owner;
  kolya: number;
  vlad: number;
}
export interface ExpenseHistory {
  id: number;
  expense_id: string;
  at: string;
  actor: string;
  payload: string;
}
export interface ExpensesSnapshot {
  expenses: HistoricExpense[];
  history: ExpenseHistory[];
  accounting: AccountingSnapshot;
}
export interface Allocation {
  lot_id: string;
  amount: string;
}
export interface ReceiptSale {
  reimbursements: Shares;
  profit_parts: Shares;
  payouts: Shares;
  gross_amount_minor: number;
  report_parts_minor: { purchase: number; other: number; profit: number };
}
export interface ReceiptPreview {
  preview_token: string;
  delivery_amount_minor: number;
  delivery_total_minor: number;
  account_id?: string;
  payer_party: Owner;
  items: {
    sku: string;
    delivery_added_minor: number;
    delivery_total_minor: number;
    sale?: ReceiptSale;
  }[];
  totals: Pick<ReceiptSale, "reimbursements" | "profit_parts" | "payouts">;
}
export interface MoneyUser {
  can_finance: boolean;
  is_owner?: boolean;
  role: string;
  name?: string;
  timezone?: string;
  id?: string | number;
  user_id?: string | number;
}
export type MoneySection =
  "balances" | "money" | "cycles" | "expenses" | "purchases" | "shipments";
export const people = { kolya: "Коля", vlad: "Влад" };
export const stages: Record<string, string> = {
  untracked: "Начальный остаток",
  purchased: "Едет на проверку",
  inspection: "На проверке",
  china_transit: "Едет на склад в Китае",
  china_warehouse: "Склад в Китае",
  in_transit: "В пути",
  moscow: "Получена в Москве",
  processing: "Разбор и подготовка",
  ready: "Готова к продаже",
  forming: "Формируется",
  received: "Получена",
  cancelled: "Отменена",
  received_open: "Получена · состав открыт",
  received_closed: "Получена · состав закрыт",
};

export function shipmentStageLabel(s: {
  status: string;
  composition_closed_at?: string | null;
}): string {
  if (s.status === "received")
    return s.composition_closed_at
      ? "Получена · состав закрыт"
      : "Получена · состав открыт";
  return stages[s.status] || s.status;
}

export function shipmentItemTitle(item: ShipmentItem): string {
  return item.description || item.title || "Сумка";
}

export function shipmentHasPreorder(s: { items: ShipmentItem[] }): boolean {
  return s.items.some((i) => i.fulfillment_mode === "preorder_direct");
}

export function shipmentWaitsSettlement(s: { items: ShipmentItem[] }): boolean {
  return s.items.some((i) => i.settlement_status === "pending_delivery");
}

export function settlementLabel(status?: string | null): string | null {
  if (status === "pending_delivery") return "ждёт расчёта";
  if (status === "settled") return "рассчитана";
  return null;
}

export function fulfillmentLabel(mode?: string | null): string {
  if (mode === "preorder_direct") return "предзаказ";
  return "маркетплейс";
}

/** A2 tabs for the shipments screen. */
export const shipmentTabs = [
  {
    value: "all",
    label: "Все",
    emptyTitle: "Поставок пока нет",
    emptyHint: "Выберите сумки на складе Китая и зарегистрируйте отправленную посылку.",
  },
  {
    value: "forming",
    label: "Ранее начатые",
    emptyTitle: "Ранее начатых поставок нет",
    emptyHint: "Новые отправки регистрируются сразу из склада Китая.",
  },
  {
    value: "in_transit",
    label: "В пути",
    emptyTitle: "В пути пусто",
    emptyHint: "Отправленные поставки будут здесь.",
  },
  {
    value: "moscow",
    label: "В Москве",
    emptyTitle: "В Москве пусто",
    emptyHint: "Принятые поставки с открытым составом — здесь.",
  },
  {
    value: "closed",
    label: "История",
    emptyTitle: "Принятых поставок пока нет",
    emptyHint: "После приёмки поставки сохраняются здесь.",
  },
  {
    value: "cancelled",
    label: "Отменённые",
    emptyTitle: "Отменённых нет",
    emptyHint: "Отменённые поставки появятся здесь.",
  },
] as const;

export function shipmentDeliveryStatus(s: Shipment): ShipmentDeliveryStatus {
  if (s.delivery_status) return s.delivery_status;
  if (s.status === "received" && s.delivery_total_minor != null)
    return "paid_legacy";
  return s.delivery_total_minor == null ? "not_set" : "pending";
}

export function shipmentDeliveryStatusLabel(s: Shipment): string {
  const status = shipmentDeliveryStatus(s);
  if (status === "not_set") return "Стоимость не указана";
  if (status === "pending") return "Ожидает оплаты";
  if (status === "partially_paid") return "Оплачена частично";
  return "Оплачена";
}

export function shipmentStageKey(s: {
  status: string;
  composition_closed_at?: string | null;
}): string {
  if (s.status === "received")
    return s.composition_closed_at ? "closed" : "moscow";
  if (s.status === "forming" || s.status === "in_transit") return s.status;
  return s.status;
}

export function shipmentEmptyCopy(
  tab: string,
  query: string,
): { title: string; hint: string } {
  if (query.trim()) {
    return { title: "Ничего не найдено", hint: "Сбросьте поиск." };
  }
  const item = shipmentTabs.find((x) => x.value === tab);
  return {
    title: item?.emptyTitle || "Пока нет поставок",
    hint: item?.emptyHint || "В этом разделе пусто.",
  };
}

export const purchaseTabs = [
  {
    value: "active",
    label: "Все в дороге",
    emptyTitle: "Все сумки приняты",
    emptyHint: "Сумки на китайском складе доступны в разделе «Поставки».",
  },
  {
    value: "inspection",
    label: "Едут на проверку",
    emptyTitle: "Нет сумок на проверке",
    emptyHint: "Здесь появятся сумки, выкупленные с проверкой.",
  },
  {
    value: "china_transit",
    label: "Едут на склад",
    emptyTitle: "Нет сумок в дороге на склад",
    emptyHint: "Здесь появятся сумки без проверки или после её завершения.",
  },
  {
    value: "history",
    label: "История",
    emptyTitle: "История пуста",
    emptyHint: "Все документы закупок, включая завершённые и отменённые, сохраняются здесь.",
  },
] as const;

const purchaseTabStages: Record<string, readonly string[]> = {
  actions: ["untracked", "legacy"],
  purchased: ["purchased"],
  china: ["inspection", "china_warehouse", "forming"],
  transit: ["in_transit"],
  moscow: ["moscow", "processing"],
  ready: ["ready"],
  history: ["reserved", "sold", "cancelled", "voided"],
};

export function purchaseStageKey(stage: string): string {
  for (const [tab, stages] of Object.entries(purchaseTabStages)) {
    if (stages.includes(stage)) return tab;
  }
  return stage;
}

export function purchaseEmptyCopy(
  tab: string,
  query: string,
): { title: string; hint: string } {
  if (query.trim()) {
    return { title: "Ничего не найдено", hint: "Сбросьте поиск." };
  }
  const item = purchaseTabs.find((x) => x.value === tab);
  return {
    title: item?.emptyTitle || "Пока нет закупок",
    hint: item?.emptyHint || "В этом разделе пусто.",
  };
}


/** A2 tabs: capital in/out history. */
export const capitalTabs = [
  { value: "all", label: "Все", emptyTitle: "История пуста", emptyHint: "Вводы, выводы и сверки появятся здесь." },
  { value: "in", label: "Вводы", emptyTitle: "Вводов нет", emptyHint: "Внесённые суммы будут здесь." },
  { value: "out", label: "Выводы", emptyTitle: "Выводов нет", emptyHint: "Выведенные суммы будут здесь." },
  { value: "reconcile", label: "Сверки", emptyTitle: "Сверок нет", emptyHint: "Сверки рублёвых счетов появятся здесь." },
] as const;

export const capitalOwnerTabs = [
  { value: "all", label: "Все" },
  { value: "kolya", label: "Коля" },
  { value: "vlad", label: "Влад" },
] as const;

/** A2 tabs: cycles list. */
export const cycleTabs = [
  { value: "open", label: "Открытые", emptyTitle: "Открытых циклов нет", emptyHint: "Новый цикл появится на этой вкладке." },
  { value: "closed", label: "Закрытые", emptyTitle: "Закрытых циклов нет", emptyHint: "Закрытые циклы будут здесь." },
  { value: "voided", label: "Отменённые", emptyTitle: "Отменённых нет", emptyHint: "Отменённые циклы появятся здесь." },
] as const;

export function cycleTabKey(status: string, display?: string): string {
  const s = display || status;
  if (s === "voided" || status === "voided") return "voided";
  if (s === "closed" || status === "closed") return "closed";
  return "open";
}

/** A2 tabs: expenses ledger. */
export const expenseTabs = [
  { value: "all", label: "Все", emptyTitle: "Расходов нет", emptyHint: "Проведённые расходы появятся здесь." },
  { value: "expense", label: "Расходы", emptyTitle: "Расходов нет", emptyHint: "Обычные расходы будут здесь." },
  { value: "fees", label: "Комиссии", emptyTitle: "Комиссий нет", emptyHint: "Комиссии и запасы TRX — здесь." },
] as const;

export function expenseTabKey(kind: string): string {
  return kind === "fee_reserve" || kind === "transfer" ? "fees" : "expense";
}

export const historicExpenseTabs = [
  { value: "active", label: "Активные", emptyTitle: "Активных записей нет", emptyHint: "Исторические расходы будут здесь." },
  { value: "cancelled", label: "Отменённые", emptyTitle: "Отменённых нет", emptyHint: "Отменённые записи появятся здесь." },
] as const;


export const cycleStatuses: Record<string, string> = {
  open: "идёт",
  spend_tail: "спишите хвост",
  spend_prior_tail: "сначала старый хвост",
  ready_to_close: "можно закрыть",
  closed: "закрыт",
  voided: "отменён",
};
export const kinds: Record<string, string> = {
  opening: "Начальный остаток",
  deposit: "Внесение денег",
  exchange: "Обмен валют",
  transfer: "Перевод",
  expense: "Расход",
  product_expense: "Расход на сумку",
  fee_reserve: "Комиссия TRX / сети",
  fee_reserve_finish: "Запас комиссий израсходован",
  reconcile: "Сверка остатка",
  settle: "Взаиморасчёт",
  purchase: "Закупка",
  sale: "Продажа",
  delivery: "Доставка",
  shipment_delivery: "Оплата доставки",
  cycle_close: "Закрытие цикла",
  reversal: "Сторнирование",
};
export const categories: Record<string, string> = {
  advertising: "Реклама",
  staff: "Сотрудник",
  services: "Сервер и сервисы",
  other: "Другое",
};
export const ownerAccount = (owner: Owner) =>
  owner === "kolya" ? "kolya_card_rub" : "vlad_cash_rub";
export const digits = (currency: Currency = "RUB") =>
  currency === "RUB" ? 0 : currency === "USDT" ? 1 : 2;
export const validMoneyInput = (value: string, precision: number) =>
  (precision === 0 ? /^\d*$/ : new RegExp(`^\\d*(?:[.,]\\d{0,${precision}})?$`)).test(value);
/** Independent floor shares; the unallocated remainder stays on the document. */
export function floorShares(total: number, weights: number[]) {
  if (!Number.isSafeInteger(total) || total < 0 || weights.some(w => !Number.isSafeInteger(w) || w < 0))
    throw Error("Некорректная сумма распределения.");
  const sum = weights.reduce((a, b) => a + BigInt(b), 0n);
  return sum ? weights.map(w => Number(BigInt(total) * BigInt(w) / sum)) : weights.map(() => 0);
}
export function minor(value: string | number, currency: Currency = "RUB") {
  const source = String(value).trim().replace(",", ".");
  const precision = digits(currency);
  if (!/^\d+(\.\d*)?$/.test(source))
    throw Error("Укажите неотрицательную сумму.");
  const [whole, part = ""] = source.split(".");
  if (part.length > precision)
    throw Error(`Допустимо ${precision} знаков после запятой (${currency}).`);
  const result = Number(
    BigInt(whole) * 10n ** BigInt(precision) +
      BigInt(part.padEnd(precision, "0")),
  );
  if (!Number.isSafeInteger(result)) throw Error("Слишком большая сумма.");
  return result;
}
export const decimal = (value: number, currency: Currency = "RUB") =>
  (value / 10 ** digits(currency)).toFixed(digits(currency));
export const money = (
  value: number | null | undefined,
  currency: Currency = "RUB",
) =>
  value == null
    ? "—"
    : `${new Intl.NumberFormat("ru-RU", { minimumFractionDigits: currency === "USDT" ? 1 : 0, maximumFractionDigits: digits(currency) }).format(value / 10 ** digits(currency))} ${currency === "RUB" ? "₽" : currency}`;
export const when = (value?: string, timezone?: string) =>
  value
    ? new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "short",
        timeStyle: "short",
        timeZone: timezone,
      }).format(new Date(value))
    : "—";
export function availableLots(
  a: AccountingSnapshot,
  accountId: string,
  owner: Owner,
) {
  return a.lots.filter(
    (l) =>
      l.account_id === accountId &&
      l.owner_party === owner &&
      l.status === "open" &&
      l.remaining_amount_minor > 0,
  );
}
export function fifo(
  a: AccountingSnapshot,
  accountId: string,
  owner: Owner,
  amount: number,
): Allocation[] {
  const closed = new Set(
    a.cycles.filter((c) => c.status === "closed").map((c) => c.id),
  );
  const list = availableLots(a, accountId, owner)
    .slice()
    .sort(
      (x, y) =>
        Number(closed.has(y.cycle_id || "")) -
          Number(closed.has(x.cycle_id || "")) ||
        x.acquired_at.localeCompare(y.acquired_at) ||
        x.id.localeCompare(y.id),
    );
  const groups = new Map<string, Lot[]>();
  for (const lot of list) {
    const key =
      (closed.has(lot.cycle_id || "") ? "tail:" : "") +
      (lot.source_document_id || lot.id);
    groups.set(key, [...(groups.get(key) || []), lot]);
  }
  let left = amount;
  const result: Allocation[] = [];
  for (const batch of groups.values()) {
    if (left <= 0) break;
    const total = batch.reduce((n, l) => n + l.remaining_amount_minor, 0),
      take = Math.min(left, total);
    const parts = batch.map((l) => ({
      lot: l,
      amount: Number(
        (BigInt(take) * BigInt(l.remaining_amount_minor)) / BigInt(total),
      ),
      rest: (BigInt(take) * BigInt(l.remaining_amount_minor)) % BigInt(total),
    }));
    let extra = take - parts.reduce((n, p) => n + p.amount, 0);
    for (const p of [...parts].sort((x, y) =>
      x.rest < y.rest ? 1 : x.rest > y.rest ? -1 : 0,
    ))
      if (extra-- > 0) p.amount++;
    for (const p of parts)
      if (p.amount)
        result.push({
          lot_id: p.lot.id,
          amount: decimal(p.amount, p.lot.currency),
        });
    left -= take;
  }
  if (left > 0)
    throw Error("Доступных денег выбранного владельца недостаточно.");
  return result;
}
export function fundingCost(lot: Lot, amount: number) {
  if (lot.rub_cost_minor == null)
    throw Error("У партии не указана рублёвая стоимость.");
  const used = BigInt(lot.initial_amount_minor - lot.remaining_amount_minor),
    total = BigInt(lot.initial_amount_minor),
    cost = BigInt(lot.rub_cost_minor);
  return Number(
    (cost * (used + BigInt(amount))) / total - (cost * used) / total,
  );
}
